/**
 * Cheap audio duration preflight: an <audio> element reads only the
 * container metadata, versus decodeAudioData inflating the whole file to
 * PCM (multi-GB for hours-long recordings). Lets the 2-hour cap reject
 * over-length files before the expensive decode.
 *
 * Needs `media-src blob:` in the CSP (scripts/generate-headers.mjs).
 */

const PROBE_TIMEOUT_MS = 5_000;

/**
 * Duration in seconds, or null when it can't be determined cheaply
 * (unsupported container, missing metadata, streamed files reporting
 * Infinity, timeout). Callers must treat null as "unknown", not "short".
 */
export function probeDurationSec(file: File): Promise<number | null> {
  if (typeof Audio === "undefined" || typeof URL.createObjectURL !== "function") {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    const finish = (value: number | null) => {
      clearTimeout(timer);
      audio.onloadedmetadata = null;
      audio.onerror = null;
      audio.removeAttribute("src");
      URL.revokeObjectURL(url);
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), PROBE_TIMEOUT_MS);
    audio.preload = "metadata";
    audio.onloadedmetadata = () => {
      finish(Number.isFinite(audio.duration) ? audio.duration : null);
    };
    audio.onerror = () => finish(null);
    audio.src = url;
  });
}
